// Main.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Link,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  ContentPaste as ContentPasteIcon,
  Home as HomeIcon,
  NoteAdd as NoteAddIcon,
} from "@mui/icons-material";

import FileGrid, { encodeKey, FileItem, isDirectory } from "./FileGrid";
import MoveConflictDialog from "./MoveConflictDialog";
import MoveDestinationDialog from "./MoveDestinationDialog";
import MoveProgressDialog from "./MoveProgressDialog";
import MoveResultDialog from "./MoveResultDialog";
import MultiSelectToolbar from "./MultiSelectToolbar";
import UploadDrawer, { UploadFab } from "./UploadDrawer";
import TextPadDrawer from "./TextPadDrawer";
import {
  fetchPath,
  movePaths,
  prepareMove,
  renamePath,
} from "./app/transfer";
import {
  MoveConflict,
  MoveConflictPolicy,
  MoveProgress,
  MoveResult,
  MoveSource,
  validateMoveDestination,
} from "./app/move";
import { useTransferQueue, useUploadEnqueue } from "./app/transferQueue";

// Centered helper
function Centered({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      {children}
    </Box>
  );
}

// Breadcrumb component
function PathBreadcrumb({
  path,
  onCwdChange,
}: {
  path: string;
  onCwdChange: (newCwd: string) => void;
}) {
  const parts = path.replace(/\/$/, "").split("/");

  return (
    <Breadcrumbs separator="›" sx={{ padding: 1 }}>
      <Button onClick={() => onCwdChange("")} sx={{ minWidth: 0, padding: 0 }}>
        <HomeIcon />
      </Button>
      {parts.map((part, index) =>
        index === parts.length - 1 ? (
          <Typography key={index} color="text.primary">
            {part}
          </Typography>
        ) : (
          <Link
            key={index}
            component="button"
            onClick={() => {
              onCwdChange(parts.slice(0, index + 1).join("/") + "/");
            }}
          >
            {part}
          </Link>
        )
      )}
    </Breadcrumbs>
  );
}

// DropZone wrapper
function DropZone({
  children,
  onDrop,
}: {
  children: React.ReactNode;
  onDrop: (files: FileList) => void;
}) {
  const [dragging, setDragging] = useState(false);

  return (
    <Box
      sx={{
        flexGrow: 1,
        overflowY: "auto",
        backgroundColor: (theme) => theme.palette.background.default,
        filter: dragging ? "brightness(0.9)" : "none",
        transition: "filter 0.2s",
      }}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(e.dataTransfer.files);
        setDragging(false);
      }}
    >
      {children}
    </Box>
  );
}

// Main Component
function Main({
  search,
  onError,
  onReadonlyChange,
}: {
  search: string;
  onError: (error: Error) => void;
  onReadonlyChange?: (readOnly: boolean) => void;
}) {
  const [cwd, setCwd] = useState("");
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [multiSelected, setMultiSelected] = useState<string[] | null>(null);
  const [showUploadDrawer, setShowUploadDrawer] = useState(false);
  const [showTextPadDrawer, setShowTextPadDrawer] = useState(false);
  const [lastUploadKey, setLastUploadKey] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const [cutBuffer, setCutBuffer] = useState<MoveSource[] | null>(null);
  const [destinationSources, setDestinationSources] = useState<
    MoveSource[] | null
  >(null);
  const [pendingMove, setPendingMove] = useState<{
    sources: MoveSource[];
    destination: string;
    origin: "picker" | "paste";
  } | null>(null);
  const [moveConflicts, setMoveConflicts] = useState<MoveConflict[]>([]);
  const [moveProgress, setMoveProgress] = useState<MoveProgress | null>(null);
  const [moveResult, setMoveResult] = useState<MoveResult | null>(null);
  const [preparingMove, setPreparingMove] = useState(false);

  const transferQueue = useTransferQueue();
  const uploadEnqueue = useUploadEnqueue();

  const fetchFiles = useCallback(() => {
    return fetchPath(cwd, "1", {
      onReadonly: (value) => {
        setReadOnly(value);
        onReadonlyChange?.(value);
      },
    })
      .then((files) => {
        setFiles(files);
        setMultiSelected(null);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, [cwd, onError, onReadonlyChange]);

  useEffect(() => setLoading(true), [cwd]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => {
    if (!transferQueue.length) return;
    const lastFile = transferQueue[transferQueue.length - 1];
    if (["pending", "in-progress"].includes(lastFile.status)) {
      setLastUploadKey(lastFile.remoteKey);
    } else if (lastUploadKey) {
      fetchFiles();
      setLastUploadKey(null);
    }
  }, [cwd, fetchFiles, lastUploadKey, transferQueue]);

  useEffect(() => {
    if (!moveProgress) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [moveProgress]);

  useEffect(() => {
    if (readOnly) setCutBuffer(null);
  }, [readOnly]);

  const filteredFiles = useMemo(
    () =>
      (search
        ? files.filter((file) =>
            file.key.toLowerCase().includes(search.toLowerCase())
          )
        : files
      ).sort((a, b) => (isDirectory(a) ? -1 : isDirectory(b) ? 1 : 0)),
    [files, search]
  );

  const handleMultiSelect = useCallback((key: string) => {
    setMultiSelected((prev) => {
      if (prev === null) return [key];
      if (prev.includes(key)) {
        const updated = prev.filter((k) => k !== key);
        return updated.length ? updated : null;
      }
      return [...prev, key];
    });
  }, []);

  const mutationActive = preparingMove || moveProgress !== null;

  const selectedMoveSources = () =>
    (multiSelected ?? []).flatMap((key) => {
      const file = files.find((item) => item.key === key);
      return file ? [{ key, isDirectory: isDirectory(file) }] : [];
    });

  const executeMove = async (
    sources: MoveSource[],
    destination: string,
    conflictPolicy: MoveConflictPolicy,
    conflicts: MoveConflict[]
  ) => {
    setDestinationSources(null);
    setPendingMove(null);
    setMoveConflicts([]);
    try {
      const result = await movePaths({
        sources,
        destination,
        conflictPolicy,
        conflicts,
        onProgress: setMoveProgress,
      });
      setMoveResult(result);
      const succeeded = new Set(result.succeeded);
      setCutBuffer((current) => {
        const remaining =
          current?.filter(({ key }) => !succeeded.has(key)) ?? [];
        return remaining.length ? remaining : null;
      });
      await fetchFiles();
      if (result.failed.length) {
        onError(new Error(`${result.failed.length} item(s) failed to move`));
      }
    } catch (error) {
      onError(error instanceof Error ? error : new Error("Move failed"));
    } finally {
      setMoveProgress(null);
    }
  };

  const beginMove = async (
    sources: MoveSource[],
    destination: string,
    origin: "picker" | "paste"
  ) => {
    if (!sources.length || mutationActive) return;
    const invalid = validateMoveDestination(sources, destination);
    if (invalid.length) {
      onError(
        new Error(`Invalid move destination: ${invalid.join(", ")}`)
      );
      return;
    }

    setPreparingMove(true);
    try {
      const conflicts = await prepareMove(sources, destination);
      setDestinationSources(null);
      if (conflicts.length) {
        setPendingMove({ sources, destination, origin });
        setMoveConflicts(conflicts);
      } else {
        await executeMove(sources, destination, "skip", []);
      }
    } catch (error) {
      onError(error instanceof Error ? error : new Error("Move failed"));
    } finally {
      setPreparingMove(false);
    }
  };

  return (
    <>
      {cwd && <PathBreadcrumb path={cwd} onCwdChange={setCwd} />}

      {loading ? (
        <Centered>
          <CircularProgress />
        </Centered>
      ) : (
        <DropZone
          onDrop={(files) => {
            if (readOnly) return;
            uploadEnqueue(
              ...Array.from(files).map((file) => ({ file, basedir: cwd }))
            );
          }}
        >
          <FileGrid
            files={filteredFiles}
            onCwdChange={(newCwd: string) => setCwd(newCwd)}
            multiSelected={multiSelected}
            onMultiSelect={handleMultiSelect}
            emptyMessage={<Centered>No files or folders</Centered>}
          />
        </DropZone>
      )}

      {multiSelected === null && !cutBuffer && !readOnly && !loading && (
        <>
          <UploadFab onClick={() => setShowUploadDrawer(true)} />
          <Button
            variant="contained"
            startIcon={<NoteAddIcon />}
            sx={{
              position: "fixed",
              bottom: 90,
              right: 24,
              zIndex: 999,
            }}
            onClick={() => setShowTextPadDrawer(true)}
          >
            Open TextPad
          </Button>
        </>
      )}

      {!readOnly && (
        <UploadDrawer
          open={showUploadDrawer}
          setOpen={setShowUploadDrawer}
          cwd={cwd}
          onUpload={fetchFiles}
        />
      )}

      {!readOnly && (
        <TextPadDrawer
          open={showTextPadDrawer}
          setOpen={setShowTextPadDrawer}
          cwd={cwd}
          onUpload={fetchFiles}
        />
      )}

      {cutBuffer && !readOnly && (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 99,
            display: "flex",
            gap: 1,
            padding: 1,
            borderRadius: 2,
            backgroundColor: "background.paper",
            boxShadow: 3,
          }}
        >
          <Button
            aria-label="Paste here"
            variant="contained"
            startIcon={<ContentPasteIcon />}
            disabled={
              mutationActive ||
              validateMoveDestination(cutBuffer, cwd).length > 0
            }
            onClick={() => beginMove(cutBuffer, cwd, "paste")}
          >
            Paste here
          </Button>
          <Button
            aria-label="Cancel cut"
            startIcon={<CloseIcon />}
            disabled={mutationActive}
            onClick={() => setCutBuffer(null)}
          >
            Cancel cut
          </Button>
        </Box>
      )}

      <MoveDestinationDialog
        open={Boolean(destinationSources)}
        sources={destinationSources ?? []}
        onCancel={() => setDestinationSources(null)}
        onConfirm={(destination) => {
          if (destinationSources)
            beginMove(destinationSources, destination, "picker");
        }}
      />
      <MoveConflictDialog
        open={moveConflicts.length > 0}
        conflicts={moveConflicts}
        onCancel={() => {
          if (pendingMove?.origin === "picker") {
            setDestinationSources(pendingMove.sources);
          }
          setPendingMove(null);
          setMoveConflicts([]);
        }}
        onChoose={(policy) => {
          if (pendingMove) {
            executeMove(
              pendingMove.sources,
              pendingMove.destination,
              policy,
              moveConflicts
            );
          }
        }}
      />
      <MoveProgressDialog progress={moveProgress} />
      <MoveResultDialog
        result={moveResult}
        onClose={() => setMoveResult(null)}
      />

      <MultiSelectToolbar
        multiSelected={multiSelected}
        readOnly={readOnly}
        busy={mutationActive}
        onClose={() => setMultiSelected(null)}
        onDownload={() => {
          if (multiSelected?.length !== 1) return;
          const a = document.createElement("a");
          a.href = `/webdav/${encodeKey(multiSelected[0])}`;
          a.download = multiSelected[0].split("/").pop()!;
          a.click();
        }}
        onRename={async () => {
          if (multiSelected?.length !== 1 || mutationActive) return;
          const source = multiSelected[0];
          const selectedFile = files.find((file) => file.key === source);
          if (!selectedFile) return;
          const newName = window.prompt("Rename to:");
          if (!newName) return;
          const target = cwd + newName;
          if (target === source) return;
          try {
            await renamePath({
              source,
              target,
              sourceIsDirectory: isDirectory(selectedFile),
              onMoveProgress: setMoveProgress,
            });
            await fetchFiles();
          } catch (error) {
            onError(error instanceof Error ? error : new Error("Rename failed"));
          } finally {
            setMoveProgress(null);
          }
        }}
        onDelete={async () => {
          if (!multiSelected?.length || mutationActive) return;
          const filenames = multiSelected
            .map((key) => key.replace(/\/$/, "").split("/").pop())
            .join("\n");
          const confirmMessage = "Delete the following file(s) permanently?";
          if (!window.confirm(`${confirmMessage}\n${filenames}`)) return;
          for (const key of multiSelected)
            await fetch(`/webdav/${encodeKey(key)}`, { method: "DELETE" });
          fetchFiles();
        }}
        onShare={() => {
          if (multiSelected?.length !== 1) return;
          const url = new URL(
            `/webdav/${encodeKey(multiSelected[0])}`,
            window.location.href
          );
          navigator.share({ url: url.toString() });
        }}
        onMove={() => {
          if (mutationActive) return;
          const sources = selectedMoveSources();
          if (sources.length) setDestinationSources(sources);
        }}
        onCut={() => {
          if (mutationActive) return;
          const sources = selectedMoveSources();
          if (!sources.length) return;
          setCutBuffer(sources);
          setMultiSelected(null);
        }}
      />
    </>
  );
}

export default Main;
